<template>

    <Toast/>

    <MenuFinancialCompact v-if="userStore.user?.roles?.includes('ROLE_ISORISE')" />
    <MenuHealthCompact v-else />

    <div class="px-6 surface-overlay relative">

        <div v-show="activeTab === 1" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <ul class="list-none py-2 px-0 m-0 flex align-items-center font-medium overflow-x-auto w-full">
                        <li class="pr-3"><a class="cursor-pointer">Operational Clarity</a></li>
                        <li class="px-2"><i class="pi pi-angle-right text-900"></i></li>
                        <li class="px-2"><a class="cursor-pointer text-900 white-space-nowrap">Client Segments</a></li>
                    </ul>

                    <h3>Manage Client Segments</h3>

                    <div class="grid">
                        <ul class="list-none p-0 m-0 flex lg:align-items-center flex-column lg:flex-row select-none">
                            <li>
                                <a @click="callNewClientSegment" v-ripple
                                   class="flex p-3 align-items-center text-600 hover:text-900 hover:surface-100 font-medium border-round cursor-pointer transition-colors transition-duration-150 p-ripple">
                                    <i class="pi pi-plus-circle mr-2"></i>
                                    <span>New Client Segment</span>
                                </a>
                            </li>
                        </ul>
                    </div>

                    <DataTable
                        :value="clientSegments"
                        :paginator="true"
                        class="p-datatable-gridlines"
                        :rows="100"
                        dataKey="id"
                        :rowsPerPageOptions="[10,20,50,100]"
                        :rowHover="true"
                        :striped-rows="true"
                        :reorderableColumns="true"
                        :sortMode="'multiple'"
                        v-model:filters="filters"
                        filterDisplay="menu"
                        :loading="loading"
                        responsiveLayout="scroll"
                        :globalFilterFields="['name']">

                        <template #header>
                            <div class="p-grid">
                                <div class="grid">
                                    <div class="col-12 lg:col-6 p-2">
                                        <MultiSelect :modelValue="selectedColumns" :options="columnsFullList"
                                                     optionLabel="header"
                                                     @update:modelValue="onToggleColumns"
                                                     display="chip" placeholder="Select Columns"/>
                                    </div>
                                    <div class="col-12 lg:col-6 p-2">
                                        <MultiSelect :modelValue="selectedStandardFilter" :options="standardFilters"
                                                     optionLabel="header"
                                                     @update:modelValue="onToggleStandardFilters"
                                                     display="chip" placeholder="Select Standard Filter"/>
                                    </div>
                                </div>

                                <div class="grid">
                                    <div class="col-12 lg:col-6 p-2">
                                        <Button type="button" icon="pi pi-filter-slash" label="Clear Search"
                                                class="p-button-outlined p-ml-2" @click="clearFilter()"/>
                                    </div>
                                    <div class="col-12 lg:col-6 p-2">
                                        <Button type="button" icon="pi pi-replay" label="Restore to Original View"
                                                class="p-button-outlined" @click="restoreToOriginal()"/>
                                    </div>
                                </div>

                                <div class="p-col-12 p-md-2">
                                    <span class="p-input-icon-left">
                                        <i class="pi pi-search"/>
                                        <InputText v-model="filters['global'].value"
                                                   placeholder="Keyword Search on Name"
                                                   style="width: 100%"/>
                                    </span>
                                </div>
                            </div>

                        </template>

                        <template #empty>
                            No client segments found.
                        </template>

                        <template #loading>
                            Loading client segments. Please wait.
                        </template>

                        <Column
                            field="selectAnOption"
                            header=""
                            :headerStyle="{ width: '2rem' }"
                            :bodyStyle="{ width: '2rem', padding: '0.25rem' }">
                            <template #body="{ data, index }">
                                <Button icon="pi pi-ellipsis-v" class="p-button-sm p-button-text p-button-rounded m-0"
                                        @click="$refs['menu' + index].toggle($event)"/>
                                <Menu
                                    :ref="'menu' + index"
                                    :model="getFilteredItems(data)"
                                    :popup="true"/>
                            </template>
                        </Column>

                        <template v-for="col in selectedColumns" :key="col.field">

                            <Column v-if="col.field === 'status'" :field="col.field" :header="col.header"
                                    :sortable="col.sortable">
                                <template #body="{data}">
                                    <span class="p-column-title">Status</span>
                                    <span v-if="data.status === 'A'"
                                          :class="'status-badge status-' + data.status">Active</span>
                                    <span v-if="data.status === 'I'"
                                          :class="'status-badge status-' + data.status">Inactive</span>
                                </template>
                            </Column>

                            <Column v-else :field="col.field" :header="col.header" :sortable="col.sortable"/>

                        </template>

                    </DataTable>

                </div>
            </div>
        </div>

        <div v-show="activeTab === 2" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Update Client Segment {{ this.clientSegment.name }}</h3>

                    <div class="field">
                        <label for="name">Name</label>
                        <InputText id="name" v-model.trim="clientSegment.name" required="true"/>
                    </div>

                    <div class="field">
                        <label for="status">Status</label>
                        <Dropdown v-model="clientSegment.status" :options="statusTypes" optionLabel="name"
                                  optionValue="code"
                                  placeholder="Select a Status"/>
                    </div>

                    <Button label="Update" class="mr-2 mb-2" :disabled="!canUpdateClientSegment" :loading="submitted"
                            @click="updateClientSegment"></Button>
                    <Button label="Cancel" class="mr-2 mb-2" :loading="submitted" @click="callReset"></Button>

                </div>
            </div>
        </div>

        <div v-show="activeTab === 3" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Delete Client Segment {{ this.clientSegment.name }}</h3>

                    <div class="field">
                        <label for="confirmationResponse">Do You Wish to Delete this Client Segment?</label>
                        <Dropdown v-model="confirmationResponse" :options="confirmationTypes"
                                  optionLabel="name" optionValue="code"
                                  placeholder="Select a Response"/>
                    </div>

                    <Button label="Delete" class="mr-2 mb-2" :loading="submitted" @click="deleteClientSegment"></Button>
                    <Button label="Cancel" class="mr-2 mb-2" :loading="submitted" @click="callReset"></Button>

                </div>
            </div>
        </div>

        <div v-show="activeTab === 4" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Create Client Segment</h3>

                    <div class="field">
                        <label for="name">Name</label>
                        <InputText id="name" v-model.trim="newClientSegment.name" required="true"/>
                    </div>

                    <div class="field">
                        <label for="recipientType">Recipient Type</label>
                        <Dropdown v-model="newClientSegment.recipientType" :options="receipientTypes"
                                  optionLabel="name" optionValue="code" placeholder="Select a Type"/>
                    </div>

                    <div v-if="newClientSegment.recipientType === 'L'" class="field">
                        <label for="recipientLabel">Label</label>
                        <MultiSelect
                            v-model="newClientSegment.labels"
                            :options="labels"
                            optionLabel="name"
                            optionValue="id"
                            placeholder="Select Labels"
                            display="chip"
                            :filter="true"/>

                    </div>

                    <div v-if="newClientSegment.recipientType === 'R'" class="field">
                        <label for="recipientProduct">Product</label>
                        <MultiSelect
                            v-model="newClientSegment.products"
                            :options="products"
                            optionLabel="name"
                            optionValue="id"
                            placeholder="Select Products"
                            display="chip"
                            :filter="true"/>

                    </div>

                    <Button label="Create" class="mr-2 mb-2" :disabled="!canCreateClientSegment" :loading="submitted"
                            @click="createClientSegment"></Button>
                    <Button label="Cancel" class="mr-2 mb-2" :loading="submitted" @click="callReset"></Button>

                </div>
            </div>
        </div>


        <div v-show="activeTab === 5" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>View Clients for Client Segment {{ this.clientSegment.name }}</h3>

                    <div class="grid">
                        <ul class="list-none p-0 m-0 flex lg:align-items-center flex-column lg:flex-row select-none">
                            <li>
                                <a @click="callReset" v-ripple
                                   class="flex p-3 align-items-center text-600 hover:text-900 hover:surface-100 font-medium border-round cursor-pointer transition-colors transition-duration-150 p-ripple">
                                    <i class="pi pi-plus-circle mr-2"></i>
                                    <span>Back to Manage ClientSegments</span>
                                </a>
                            </li>
                        </ul>
                    </div>

                    <DataTable
                        :value="clientClientSegments"
                        :paginator="true"
                        class="p-datatable-gridlines"
                        :rows="100"
                        dataKey="id"
                        :rowsPerPageOptions="[10,20,50,100]"
                        :rowHover="true"
                        :striped-rows="true"
                        :reorderableColumns="true"
                        :sortMode="'multiple'"
                        v-model:filters="filters"
                        filterDisplay="menu"
                        :loading="loading"
                        responsiveLayout="scroll"
                        :globalFilterFields="['name', 'description']">

                        <template #empty>
                            No clients found for client segment.
                        </template>

                        <template #loading>
                            Loading clients for client segment. Please wait.
                        </template>

                        <Column field="clientName" header="Client Name" :sortable="true"/>
                        <Column field="created" header="Created" :sortable="true"/>

                    </DataTable>

                </div>
            </div>
        </div>

    </div>

    <IsoEvolveFooter/>

</template>

<script>
import IsoEvolveFooter from "@/components/heading/authentication/IsoEvolveFooter.vue";
import MenuHealthCompact from "@/components/menu/health/MenuHealthCompact.vue";
import MenuFinancialCompact from "@/components/menu/famodule/MenuFinancialCompact.vue";
import {useUserStore} from "@/stores/UserStore";
import UserService from "@/service/UserService";
import CompanyService from "@/service/CompanyService";
import {FilterMatchMode, FilterOperator} from "primevue/api";
import ClientSegmentService from "@/service/ClientSegmentService";
import LabelService from "@/service/LabelService";
import ProductService from "@/service/ProductService";

export default {
    components: {
        MenuFinancialCompact,
        IsoEvolveFooter, MenuHealthCompact
    },
    setup() {
        const userStore = useUserStore()

        return {
            userStore
        }
    },
    created() {
        this.initFilters();

        this.columnsDefault = [
            {field: 'name', header: 'Name', sortable: true},
            {field: 'status', header: 'Status', sortable: true}
        ];

        this.columnsFullList = [
            {field: 'id', header: 'ID', sortable: true},
            {field: 'name', header: 'Name', sortable: true},
            {field: 'status', header: 'Status', sortable: true}
        ];

        this.selectedColumns = this.columnsDefault;

        this.standardFilters = [
            {field: 'active', header: 'Active'},
            {field: 'inactive', header: 'Inactive'}
        ];

    },
    async mounted() {
        await this.loadCompanyContext();
    },
    data() {

        return {
            profile: {},
            activeTab: 1,
            companyConfiguration: {},
            userConfiguration: {},
            filters: null,
            loading: true,
            submitted: false,
            filteredItemsCache: new Map(),
            labels: [],
            products: [],
            clientSegment: {},
            clientSegments: null,
            newClientSegment: {},
            originalClientSegments: null,
            confirmationResponse: 'N',
            selectedColumns: null,
            columnsDefault: null,
            columnsFullList: null,
            selectedStandardFilter: null,
            standardFilters: null,
            clientClientSegments: null,
            confirmationTypes: [
                {name: 'Yes', code: 'Y'},
                {name: 'No', code: 'N'}
            ],
            statusTypes: [
                {name: 'Active', code: 'A'},
                {name: 'Inactive', code: 'I'}
            ],
            receipientTypes: [
                {name: 'Clients (Active)', code: 'C01'},
                {name: 'Clients (Inactive)', code: 'C02'},
                {name: 'Clients (Active and Inactive)', code: 'C03'},
                {name: 'Prospects (Won)', code: 'P01'},
                {name: 'Prospects (Lost)', code: 'P02'},
                {name: 'Prospects (Initiated)', code: 'P03'},
                {name: 'Label', code: 'L'},
                {name: 'Product', code: 'R'},
            ]
        }
    },
    watch: {
        clientSegments() {
            this.filteredItemsCache.clear();
        },
        'userStore.activeCompanyId': {
            immediate: false,
            async handler(newCompanyId, oldCompanyId) {
                if (!newCompanyId || newCompanyId === oldCompanyId) {
                    return;
                }

                console.log(
                    '[Appointments][watch] Company changed:',
                    oldCompanyId,
                    '→',
                    newCompanyId
                );

                await this.loadCompanyContext();
            }
        }
    },
    computed: {
        canCreateClientSegment() {
            if ((this.newClientSegment.name) && (this.newClientSegment.recipientType)) {
                return true;
            } else {
                return false;
            }
        },
        canUpdateClientSegment() {
            if ((this.clientSegment.name) && (this.clientSegment.status)) {
                return true;
            } else {
                return false;
            }
        }
    },
    methods: {
        async loadCompanyContext() {
            try {
                // Attempt to get user data
                const userData = await UserService.getUser(this.userStore.user.id);
                this.profile = userData.data;
            } catch (e) {
                console.error("[AppointmentsNew][mounted] Error fetching user data = ", e);
                this.userStore.clearUser();
                this.$router.push('login');
                return;
            }

            this.profile.companyId = this.userStore.activeCompanyId;

            try {
                const [userConfigurationData, companyConfigurationData] = await Promise.all([
                    UserService.getUserConfig(this.userStore.user.id),
                    CompanyService.getCompanyConfiguration(this.profile.companyId),
                ]);

                this.userConfiguration = userConfigurationData.data;
                this.companyConfiguration = companyConfigurationData.data;

                await this.loadData();

                this.loading = false;

            } catch (e) {
                console.error("[ClientSegments][mounted] Error fetching additional data = ", e);
            }

            console.log("[ClientSegments][mounted] this.userConfiguration = ", this.userConfiguration);
            console.log("[ClientSegments][mounted] this.companyConfiguration = ", this.companyConfiguration);
        },
        async loadData() {
            this.submitted = true;
            try {
                const [clientSegmentData] = await Promise.all([
                    ClientSegmentService.getCompanyClientSegments(this.profile.companyId)
                ]);

                this.clientSegments = clientSegmentData.data;
                this.originalClientSegments = [...this.clientSegments];
                console.log("[ClientSegments][loadData] clientSegmentData.data = ", clientSegmentData.data);
            } catch (error) {
                console.error("[ClientSegments][loadData] Error loading client segments:", error);
                this.clientSegments = [];
                this.originalClientSegments = [];
            } finally {
                this.submitted = false;
            }
        },
        initFilters() {
            this.filters = {
                'global': {value: null, matchMode: FilterMatchMode.CONTAINS},
                'name': {
                    operator: FilterOperator.AND,
                    constraints: [{value: null, matchMode: FilterMatchMode.STARTS_WITH}]
                },
                'description': {
                    operator: FilterOperator.AND,
                    constraints: [{value: null, matchMode: FilterMatchMode.STARTS_WITH}]
                }
            }
        },
        clearFilter() {
            this.initFilters();
        },
        async restoreToOriginal() {
            await this.loadData();
            window.location.reload();
        },
        callReset() {
            this.activeTab = 1;
        },
        async callNewClientSegment() {
            this.activeTab = 4;
            this.newClientSegment = {};

            this.submitted = true;
            try {
                const [labelData, productData] = await Promise.all([
                    LabelService.getCompanyLabels(this.profile.companyId),
                    ProductService.getCompanyProducts(this.profile.companyId)
                ]);

                this.labels = [...labelData.data];
                this.products = [...productData.data];

                console.log("[ClientSegments][callNewClientSegment] this.labels = ", this.labels);
                console.log("[ClientSegments][callNewClientSegment] this.products = ", this.products);
            } catch (error) {
                console.error("[ClientSegments][callNewClientSegment] Error loading labels:", error);
                this.labels = [];
            } finally {
                this.submitted = false;
            }
        },
        callUpdateClientSegment(data) {
            this.activeTab = 2;
            this.clientSegment = data;
        },
        callDeleteClientSegment(data) {
            this.activeTab = 3;
            this.confirmationResponse = 'N';
            this.clientSegment = data;
        },
        async callViewRecipients(data) {
            // this.activeTab = 6;
            this.clientSegment = data;

            // const [clientClientSegmentData] = await Promise.all([
            //     ClientClientSegmentService.getClientsForClientSegment(this.clientSegment.id)
            // ]);
            //
            // this.clientClientSegments = clientClientSegmentData.data;
            //
            // console.log('[ClientSegments][callViewClients] this.clientClientSegments = ', this.clientClientSegments);
        },
        onToggleColumns(value) {
            this.selectedColumns = this.columnsFullList.filter(col => value.includes(col));
        },
        onToggleStandardFilters(value) {

            if (value.length > 1) {
                // Keep the last selected option
                this.selectedStandardFilter = [value[value.length - 1]];
            } else {
                this.selectedStandardFilter = value;
            }

            const isActive = value.some(item => item.field === "active");
            const isInactive = value.some(item => item.field === "inactive");

            if (this.clientSegments !== null) {

                if (isActive) {
                    this.clientSegments = this.originalClientSegments.filter(label => label.status === 'A');
                } else if (isInactive) {
                    this.clientSegments = this.originalClientSegments.filter(label => label.status === 'I');
                } else {
                    this.clientSegments = this.originalClientSegments;
                }

            } else {
                this.clientSegments = [];
            }
        },
        getFilteredItems(data) {
            if (this.filteredItemsCache.has(data.id)) {
                return this.filteredItemsCache.get(data.id);
            }

            const items = [];

            items.push({
                label: 'Update',
                class: 'mb-4 mr-4 ml-4 mt-4',
                command: () => {
                    this.callUpdateClientSegment(data);
                }
            });

            items.push({
                label: 'Delete',
                class: 'mb-4 mr-4 ml-4 mt-4',
                command: () => {
                    this.callDeleteClientSegment(data);
                }
            });

            items.push({
                label: 'View Clients',
                class: 'mb-4 mr-4 ml-4 mt-4',
                command: () => {
                    this.callViewRecipients(data);
                }
            });

            this.filteredItemsCache.set(data.id, items);

            return items;
        },
        async updateClientSegment() {
            this.submitted = true;

            if (this.clientSegment.id) {

                try {
                    var clientSegmentDto = {
                        name: this.clientSegment.name,
                        status: this.clientSegment.status
                    };

                    const response = await ClientSegmentService.updateClientSegment(this.clientSegment.id, clientSegmentDto);
                    console.log('[ClientSegments][updateClientSegment] Response = ', response);

                    await this.loadData();

                    this.$toast.add({
                        severity: 'success',
                        summary: 'Successful',
                        detail: 'Updated client segment',
                        life: 3000
                    });

                } catch (error) {
                    console.error('[ClientSegments][updateClientSegment] Error:', error);

                    this.$toast.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Failed to update client segment. Please try again.',
                        life: 3000
                    });
                }
            }

            this.activeTab = 1;
            this.submitted = false;
        },
        async deleteClientSegment() {
            this.submitted = true;

            if (this.confirmationResponse.trim() === "Y") {

                if (this.clientSegment.id) {

                    try {
                        const response = await ClientSegmentService.deleteClientSegment(this.clientSegment.id);
                        console.log('[ClientSegments][deleteClientSegment] Response = ', response);

                        await this.loadData();

                        this.$toast.add({
                            severity: 'success',
                            summary: 'Successful',
                            detail: 'Deleted client segment',
                            life: 3000
                        });

                    } catch (error) {
                        console.error('[ClientSegments][deleteClientSegment] Error:', error);

                        this.$toast.add({
                            severity: 'error',
                            summary: 'Error',
                            detail: 'Failed to delete client segment. Please try again.',
                            life: 3000
                        });
                    }
                }

            }

            this.activeTab = 1;
            this.submitted = false;
        },
        async createClientSegment() {
            this.submitted = true;

            if (this.newClientSegment.name) {

                console.log('[ClientSegments][createClientSegment] this.newClientSegment.recipientType = ', this.newClientSegment.recipientType);
                console.log('[ClientSegments][createClientSegment] this.newClientSegment.labels = ', this.newClientSegment.labels);
                console.log('[ClientSegments][createClientSegment] this.newClientSegment.products = ', this.newClientSegment.products);

                try {
                    var newClientSegmentDto = {
                        name: this.newClientSegment.name,
                        recipientType: this.newClientSegment.recipientType,
                        labels: this.newClientSegment.labels,
                        products: this.newClientSegment.products,
                        status: 'A'
                    };

                    const response =
                        await ClientSegmentService.createClientSegment(this.profile.companyId, newClientSegmentDto);
                    console.log('[ClientSegments][createClientSegment] Response = ', response);

                    await this.loadData();

                    this.$toast.add({
                        severity: 'success',
                        summary: 'Successful',
                        detail: 'Created client segment',
                        life: 3000
                    });

                } catch (error) {
                    console.error('[ClientSegments][createClientSegment] Error:', error);

                    this.$toast.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Failed to create client segment. Please try again.',
                        life: 3000
                    });
                }
            }

            this.activeTab = 1;
            this.submitted = false;
        }
    },
};
</script>

<style scoped lang="scss">
a {
    text-decoration: none;
}

::v-deep(.p-datatable-frozen-tbody) {
    font-weight: bold;
}

::v-deep(.p-datatable-scrollable .p-frozen-column) {
    font-weight: bold;
}

::v-deep(.p-progressbar) {
    height: .5rem;
    background-color: #D8DADC;

    .p-progressbar-value {
        background-color: #607D8B;
    }
}

.status-badge {
    border-radius: var(--border-radius);
    padding: .25em .5rem;
    text-transform: uppercase;
    font-weight: 700;
    font-size: 12px;
    letter-spacing: .3px;
}

.status-badge {
    &.status-I {
        background: #B3E5FC;
        color: #23547B;
    }

    &.status-A {
        background: #C8E6C9;
        color: #256029;
    }
}

.tabview-custom {
    i, span {
        vertical-align: middle;
    }

    span {
        margin: 0 .5rem;
    }
}

.p-tabview p {
    line-height: 1.5;
    margin: 0;
}

.accordion-custom {
    i, span {
        vertical-align: middle;
    }

    span {
        margin: 0 .5rem;
    }
}

.p-accordion p {
    line-height: 1.5;
    margin: 0;
}

.mb-2 {
    margin-bottom: 1rem;
}

.mr-2 {
    margin-right: 1rem;
}

</style>
